import { useState, useRef } from 'react';
import { Spinner, Btn } from '../ui/index';
import { EmojiBar } from '../ui/EmojiPicker';
import { ticketApi } from '../../api/ticket.api';

const ACTIONS = [
  { id: 'generate', label: 'تولید پاسخ', color: '#233A7A' },
  { id: 'improve',  label: 'بهبود پاسخ',  color: '#7c3aed' },
  { id: 'rewrite',  label: 'بازنویسی متن', color: '#0891b2' },
];

const AI_ASSIST_FAILED_MESSAGE = 'سرویس هوش مصنوعی پاسخ نداد (احتمالاً شلوغ است) — لطفاً دوباره تلاش کنید';

async function callAI(action, ticketId, currentDescription) {
  let data;
  if (action === 'generate') {
    const res = await ticketApi.generateAiResponse(ticketId);
    data = res.data || { title: '', description: '', error: 'no response' };
  } else if (action === 'improve') {
    const res = await ticketApi.improveAiResponse({ ticket_id: ticketId, title: '', description: currentDescription });
    data = res.data || { title: '', description: currentDescription, error: 'no response' };
  } else {
    const res = await ticketApi.rewriteAiResponse({ ticket_id: ticketId, title: '', description: currentDescription });
    data = res.data || { title: '', description: currentDescription, error: 'no response' };
  }
  // The backend reports a real failure (timeout/unreachable ai_server) via
  // `error`, falling back to empty/unchanged text - never surface that as
  // if it were a real answer. Also guards against a description-less
  // title ever being mistaken for the actual reply text (see onInsert).
  if (data.error || !data.description) {
    throw new Error(AI_ASSIST_FAILED_MESSAGE);
  }
  return data;
}

export default function AIAssistantPanel({ ticketId, onInsert }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [error, setError] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  const descRef = useRef(null);

  const run = async (action) => {
    setLoading(true); setActiveAction(action); setError('');
    try {
      const data = await callAI(action, ticketId, currentDescription);
      setResult(data);
    } catch (e) {
      setResult(null);
      setError(e.message || 'خطا در اتصال به سرویس هوش مصنوعی');
    }
    setLoading(false); setActiveAction(null);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={s.floatBtn}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2a10 10 0 110 20A10 10 0 0112 2zm-1 6v4l3 3" />
        </svg>
        دستیار هوشمند
      </button>
    );
  }

  return (
    <div style={s.panel}>
      {/* Header */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={s.aiDot} />
          <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>دستیار هوشمند پژواک</span>
          <span style={s.aiBadge}>AI</span>
        </div>
        <button onClick={() => setOpen(false)} style={s.closeBtn}>✕</button>
      </div>

      {/* Action grid */}
      <div style={s.actionGrid}>
        {ACTIONS.map((a) => (
          <button key={a.id} onClick={() => run(a.id)} disabled={loading} style={{
            ...s.actionBtn,
            borderColor: a.color + '40',
            color: a.color,
            background: a.color + '08',
            opacity: loading && activeAction !== a.id ? 0.5 : 1,
          }}>
            {loading && activeAction === a.id ? <Spinner size={12} color={a.color} /> : null}
            {a.label}
          </button>
        ))}
      </div>

      {/* Current draft input (used by improve/rewrite) */}
      <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <textarea
          ref={descRef}
          placeholder="متن فعلی پاسخ شما (برای بهبود/بازنویسی)..."
          value={currentDescription}
          onChange={(e) => setCurrentDescription(e.target.value)}
          rows={2}
          style={s.replyInput}
        />
        <EmojiBar inputRef={descRef} value={currentDescription} onInsert={setCurrentDescription} />
      </div>

      {/* Result */}
      {(result || loading || error) && (
        <div style={s.resultBox}>
          {loading && !result
            ? <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#7c3aed', fontSize: 13 }}>
                <Spinner size={16} color="#7c3aed" /> در حال تولید پاسخ...
              </div>
            : result && (
              <>
                <p style={{ fontSize: 13, lineHeight: 1.7, color: '#1e293b', whiteSpace: 'pre-wrap', margin: 0 }}>{result.description}</p>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <Btn size="sm" onClick={() => { onInsert?.({ ...result, description: result.description }); setOpen(false); }}>
                    ↓ درج در پاسخ
                  </Btn>
                  <Btn size="sm" variant="secondary" onClick={() => setResult(null)}>
                    پاک کردن
                  </Btn>
                </div>
              </>
            )
          }
          {error && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{error}</p>}
        </div>
      )}
    </div>
  );
}

const s = {
  floatBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 20,
    background: 'linear-gradient(135deg,#8b5cf6,#6d28d9)',
    color: '#fff', fontSize: 12, fontWeight: 600,
    border: 'none', cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(139,92,246,0.4)',
    fontFamily: 'inherit',
  },
  panel: {
    border: '1px solid rgba(139,92,246,0.2)',
    borderRadius: 12, background: '#fefefe',
    boxShadow: '0 8px 32px rgba(139,92,246,0.12)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 14px',
    background: 'linear-gradient(135deg,rgba(139,92,246,0.08),rgba(109,40,217,0.04))',
    borderBottom: '1px solid rgba(139,92,246,0.1)',
  },
  aiDot: { width: 8, height: 8, borderRadius: '50%', background: '#8b5cf6', boxShadow: '0 0 6px #8b5cf6' },
  aiBadge: { background: '#7c3aed', color: '#fff', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 6 },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, lineHeight: 1 },
  actionGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6,
    padding: 12,
  },
  actionBtn: {
    padding: '7px 6px', borderRadius: 8, border: '1px solid',
    fontSize: 11, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'all 0.15s',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
  },
  replyInput: {
    width: '100%', padding: '8px 10px',
    border: '1.5px solid #e2e8f0', borderRadius: 8,
    fontSize: 12, fontFamily: 'inherit', direction: 'rtl',
    resize: 'none', outline: 'none',
  },
  resultBox: {
    margin: '0 12px 12px',
    padding: 12,
    background: 'rgba(139,92,246,0.04)',
    border: '1px solid rgba(139,92,246,0.12)',
    borderRadius: 8,
  },
};
